import type { CoachingSignals } from "./coaching-signals-types";
import type {
  CommunityActivityItem,
  CommunityActivityRole,
  CommunityActivitySignal,
  CommunitySignals,
  CommunityTalkItem,
  CommunityTalksSignal,
} from "./community-signals-types";

const MAX_ROLE_CHIPS = 4;
const FEATURED_TALK_COUNT = 3;
const VISIBLE_ACTIVITY_COUNT = 6;

export type RoleChipKey =
  | "speaker"
  | "event-host"
  | "hub-team"
  | "ai-coach"
  | "certified"
  | "active-member";

export interface RoleChip {
  key: RoleChipKey;
  label: string;
  variant: "default" | "secondary";
}

export type ImpactStatKey =
  | "talks"
  | "hosted"
  | "participated"
  | "certificates";

export interface ImpactStat {
  key: ImpactStatKey;
  label: string;
  value: number;
  caption?: string;
  href: string;
}

export interface TalkAction {
  key: "watch" | "event";
  label: "Watch talk" | "View event";
  href: string;
  external: boolean;
}

export interface SplitResult<T> {
  visible: T[];
  hidden: T[];
}

export interface TalkSplit {
  featured: CommunityTalkItem[];
  additional: CommunityTalkItem[];
}

export interface ProfileCertificateAchievement {
  id: string;
  achievementID?: string | null;
  iconURL?: string | null;
  awardedOn?: string | null;
  shareURL?: string | null;
  objective?: {
    id?: string | null;
    name?: string | null;
    icon?: string | null;
  } | null;
}

// Coherent payload of the latest SHAREABLE achievement in a group. All fields
// come from the same achievement so a share never mixes one award's URL with
// another award's date or grant id.
export interface CertificateShare {
  url: string;
  awardedOn: string;
  achievementId: string;
  achievementID: string | null;
}

export interface CertificateGroup {
  key: string;
  objectiveId: string | null;
  name: string;
  iconURL: string | null;
  objectiveIcon: string | null;
  latestAwardedOn: string;
  share: CertificateShare | null;
  latestAchievementId: string;
  latestAchievementID: string | null;
  earnedCount: number;
  achievementIds: string[];
}

function toPositiveNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : 0;
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseTime(value: string | null | undefined): number {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function formatDate(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function dedupeKey(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function uniqueNonEmpty(values: readonly (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = nonEmptyString(value);
    if (!trimmed) continue;
    const key = dedupeKey(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

function buildHubTeamLabel(cities: readonly string[]): string {
  const uniqueCities = uniqueNonEmpty(cities);
  return uniqueCities.length === 1 ? `${uniqueCities[0]} hub team` : "Hub team";
}

function shouldShowActiveMember(signals: CommunitySignals | null | undefined) {
  return Boolean(
    (signals?.activity?.participationCount ?? 0) >= 3 ||
      (signals?.questions?.count ?? 0) >= 3 ||
      (signals?.connections?.count ?? 0) >= 5,
  );
}

export function deriveRoleChips(
  signals: CommunitySignals | null | undefined,
  coaching: CoachingSignals | null | undefined,
  certificateGroups: readonly CertificateGroup[] | null | undefined,
): RoleChip[] {
  const chips: Omit<RoleChip, "variant">[] = [];
  const hubTeamCities = signals?.hosting?.hubTeam?.cities ?? [];

  if ((signals?.talks?.count ?? 0) > 0) {
    chips.push({ key: "speaker", label: "Speaker" });
  }
  if ((signals?.hosting?.count ?? 0) > 0) {
    chips.push({ key: "event-host", label: "Event host" });
  }
  if (uniqueNonEmpty(hubTeamCities).length > 0) {
    chips.push({ key: "hub-team", label: buildHubTeamLabel(hubTeamCities) });
  }
  // Per the UI brief, Active member is suppressed only by a stronger COMMUNITY
  // role chip (Speaker/Event host/Hub team) — AI Coach and Certified do not
  // suppress it.
  const hasCommunityRoleChip = chips.length > 0;
  if (coaching?.isCoach) {
    chips.push({ key: "ai-coach", label: "AI Coach" });
  }
  if ((certificateGroups?.length ?? 0) > 0) {
    chips.push({ key: "certified", label: "Certified" });
  }
  if (!hasCommunityRoleChip && shouldShowActiveMember(signals)) {
    chips.push({ key: "active-member", label: "Active member" });
  }

  return chips.slice(0, MAX_ROLE_CHIPS).map((chip, index) => ({
    ...chip,
    variant: index === 0 ? "default" : "secondary",
  }));
}

// Rollout-compat normalizer: old backend payloads carry `attendance` but no
// `activity`. Until every deployed backend emits the activity union, an
// attendance-only payload still has to surface as a visible (attended-only)
// timeline rather than an empty community section.
export function deriveActivitySignal(
  signals: CommunitySignals | null | undefined,
): CommunityActivitySignal | undefined {
  if (signals?.activity) return signals.activity;

  const attendance = signals?.attendance;
  if (!attendance) return undefined;

  const items: CommunityActivityItem[] = (attendance.recent ?? []).map(
    (entry) => ({
      eventSlug: nonEmptyString(entry.eventSlug) ?? null,
      eventName: entry.title,
      date: entry.date,
      city: entry.city,
      roles: ["attended"],
    }),
  );
  const participationCount = toPositiveNumber(attendance.count);
  if (participationCount === 0 && items.length === 0) return undefined;
  return { participationCount, items };
}

export function deriveImpactStats(
  signals: CommunitySignals | null | undefined,
  certificateGroups: readonly CertificateGroup[] | null | undefined,
  options: { isCurrentUser?: boolean } = {},
): ImpactStat[] {
  const stats: ImpactStat[] = [];
  const talksCount = toPositiveNumber(signals?.talks?.count);
  const hostedCount = toPositiveNumber(signals?.hosting?.count);
  const participationCount = toPositiveNumber(
    deriveActivitySignal(signals)?.participationCount,
  );
  const certificateCount = certificateGroups?.length ?? 0;

  if (talksCount > 0) {
    stats.push({
      key: "talks",
      label: "Talks given",
      value: talksCount,
      href: "#profile-talks",
    });
  }
  if (hostedCount > 0) {
    stats.push({
      key: "hosted",
      label: "Events hosted",
      value: hostedCount,
      href: "#profile-activity",
    });
  }
  if (participationCount > 0) {
    stats.push({
      key: "participated",
      label: "Events participated",
      value: participationCount,
      caption: "Spoke, hosted, or checked in",
      href: "#profile-activity",
    });
  }
  if (certificateCount > 0) {
    stats.push({
      key: "certificates",
      label: "Certificates earned",
      value: certificateCount,
      href: "#profile-credentials",
    });
  }

  if (!options.isCurrentUser && stats.length < 2) return [];
  return stats;
}

function talkItems(
  talks: CommunityTalksSignal | readonly CommunityTalkItem[] | null | undefined,
): CommunityTalkItem[] {
  if (!talks) return [];
  return "items" in talks ? [...talks.items] : [...talks];
}

function hasTestimonials(talk: CommunityTalkItem): boolean {
  return (talk.testimonials?.length ?? 0) > 0;
}

export function rankTalksForPortfolio(
  talks: CommunityTalksSignal | readonly CommunityTalkItem[] | null | undefined,
): CommunityTalkItem[] {
  return talkItems(talks)
    .map((talk, index) => ({ talk, index }))
    .sort((a, b) => {
      const recordingDiff =
        Number(Boolean(b.talk.recordingUrl)) -
        Number(Boolean(a.talk.recordingUrl));
      if (recordingDiff !== 0) return recordingDiff;

      const testimonialDiff =
        Number(hasTestimonials(b.talk)) - Number(hasTestimonials(a.talk));
      if (testimonialDiff !== 0) return testimonialDiff;

      const ratingDiff =
        Number(typeof b.talk.avgRating === "number") -
        Number(typeof a.talk.avgRating === "number");
      if (ratingDiff !== 0) return ratingDiff;
      if (
        typeof a.talk.avgRating === "number" &&
        typeof b.talk.avgRating === "number" &&
        a.talk.avgRating !== b.talk.avgRating
      ) {
        return b.talk.avgRating - a.talk.avgRating;
      }

      const recencyDiff = parseTime(b.talk.date) - parseTime(a.talk.date);
      if (recencyDiff !== 0) return recencyDiff;

      return a.index - b.index;
    })
    .map(({ talk }) => talk);
}

export function splitFeaturedTalks(
  talks: CommunityTalksSignal | readonly CommunityTalkItem[] | null | undefined,
): TalkSplit {
  const ranked = rankTalksForPortfolio(talks);
  return {
    featured: ranked.slice(0, FEATURED_TALK_COUNT),
    additional: ranked.slice(FEATURED_TALK_COUNT),
  };
}

export function deriveTalkActions(talk: CommunityTalkItem): TalkAction[] {
  const actions: TalkAction[] = [];
  const recordingUrl = nonEmptyString(talk.recordingUrl);
  const eventSlug = nonEmptyString(talk.eventSlug);

  if (recordingUrl) {
    actions.push({
      key: "watch",
      label: "Watch talk",
      href: recordingUrl,
      external: true,
    });
  }
  if (eventSlug) {
    actions.push({
      key: "event",
      label: "View event",
      href: `/events/${eventSlug}`,
      external: false,
    });
  }

  return actions;
}

export function splitActivityTimeline(
  items: readonly CommunityActivityItem[] | null | undefined,
): SplitResult<CommunityActivityItem> {
  const sorted = [...(items ?? [])].sort((a, b) => {
    const recencyDiff = parseTime(b.date) - parseTime(a.date);
    if (recencyDiff !== 0) return recencyDiff;
    return a.eventName.localeCompare(b.eventName);
  });
  return {
    visible: sorted.slice(0, VISIBLE_ACTIVITY_COUNT),
    hidden: sorted.slice(VISIBLE_ACTIVITY_COUNT),
  };
}

export function formatRoleLabels(
  roles: readonly CommunityActivityRole[] | null | undefined,
): string[] {
  const labels: Record<CommunityActivityRole, string> = {
    spoke: "Spoke",
    hosted: "Hosted",
    attended: "Attended",
  };
  const order: CommunityActivityRole[] = ["spoke", "hosted", "attended"];
  const present = new Set(roles ?? []);
  return order.filter((role) => present.has(role)).map((role) => labels[role]);
}

export function formatEventMeta(
  city: string | null | undefined,
  dateIso: string | null | undefined,
): string {
  const tokens: string[] = [];
  const date = dateIso ? formatDate(dateIso) : null;
  if (date) tokens.push(date);

  const cityParts = nonEmptyString(city)
    ?.split(/\s+(?:·|—|-)\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (cityParts) tokens.push(...cityParts);

  return uniqueNonEmpty(tokens).join(" · ");
}

function normalizedCertificateName(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

function certificateGroupKey(achievement: ProfileCertificateAchievement): string {
  const objectiveId = nonEmptyString(achievement.objective?.id);
  if (objectiveId) return `objective:${objectiveId}`;

  const objectiveName = normalizedCertificateName(achievement.objective?.name);
  if (objectiveName) return `name:${objectiveName}`;

  return `achievement:${achievement.id}`;
}

export function groupCertificates(
  achievements: readonly ProfileCertificateAchievement[] | null | undefined,
): CertificateGroup[] {
  const groups = new Map<string, CertificateGroup>();

  for (const achievement of achievements ?? []) {
    const awardedOn = nonEmptyString(achievement.awardedOn);
    if (!awardedOn) continue;

    const key = certificateGroupKey(achievement);
    const existing = groups.get(key);
    const objectiveName = nonEmptyString(achievement.objective?.name);
    const name = objectiveName ?? "Certificate";
    const objectiveId = nonEmptyString(achievement.objective?.id);
    const shareURL = nonEmptyString(achievement.shareURL);
    const share: CertificateShare | null = shareURL
      ? {
          url: shareURL,
          awardedOn,
          achievementId: achievement.id,
          achievementID: nonEmptyString(achievement.achievementID),
        }
      : null;

    if (!existing) {
      groups.set(key, {
        key,
        objectiveId,
        name,
        iconURL: nonEmptyString(achievement.iconURL),
        objectiveIcon: nonEmptyString(achievement.objective?.icon),
        latestAwardedOn: awardedOn,
        share,
        latestAchievementId: achievement.id,
        latestAchievementID: nonEmptyString(achievement.achievementID),
        earnedCount: 1,
        achievementIds: [achievement.id],
      });
      continue;
    }

    existing.earnedCount += 1;
    existing.achievementIds.push(achievement.id);

    if (parseTime(awardedOn) >= parseTime(existing.latestAwardedOn)) {
      existing.name = name;
      existing.objectiveId = objectiveId;
      existing.iconURL =
        nonEmptyString(achievement.iconURL) ?? existing.iconURL;
      existing.objectiveIcon =
        nonEmptyString(achievement.objective?.icon) ?? existing.objectiveIcon;
      existing.latestAwardedOn = awardedOn;
      existing.latestAchievementId = achievement.id;
      existing.latestAchievementID =
        nonEmptyString(achievement.achievementID) ?? existing.latestAchievementID;
    }

    if (
      share &&
      parseTime(share.awardedOn) >= parseTime(existing.share?.awardedOn)
    ) {
      existing.share = share;
    }
  }

  return [...groups.values()].sort(
    (a, b) =>
      parseTime(b.latestAwardedOn) - parseTime(a.latestAwardedOn) ||
      a.name.localeCompare(b.name),
  );
}
